import { create } from "zustand"

import type { ReportItem } from "../config/reports-data"

/**
 * UI state for the report-purchase flow, lifted to a store so a single global
 * overlay (mounted in the webapp shell) can render the full-screen orbit spinner
 * while a charge is in flight and slide up the success sheet when it lands —
 * regardless of which reports list (Today, Reports tab, Biorhythm) triggered it.
 */
interface ReportPurchaseState {
  /** Report currently being charged; drives the processing spinner. */
  purchasingReport: ReportItem | null
  /**
   * Report whose "Report in Progress" sheet is up — right after a purchase, or
   * re-opened by tapping the still-unlocking row in a reports list.
   */
  sheetReport: ReportItem | null
  /**
   * Report whose "ready to reveal" popover is up — shown when a finished report
   * (Love Match) is tapped once its unlock timer has elapsed.
   */
  revealReport: ReportItem | null
  /** Last purchase error message (error UX handled separately). */
  error: string | null
  setPurchasing: (report: ReportItem | null) => void
  /** Mark success: hide the spinner and show the sheet in one transition. */
  setSuccess: (report: ReportItem) => void
  /** Re-open the sheet for a report whose unlock window is still running. */
  showSheet: (report: ReportItem) => void
  /** Open the "ready to reveal" popover for a finished report. */
  showReveal: (report: ReportItem) => void
  /** Mark failure: hide the spinner and record the message. */
  setError: (message: string) => void
  dismissSheet: () => void
  dismissReveal: () => void
  clearError: () => void
}

export const useReportPurchaseStore = create<ReportPurchaseState>((set) => ({
  purchasingReport: null,
  sheetReport: null,
  revealReport: null,
  error: null,
  setPurchasing: (report) => set({ purchasingReport: report }),
  setSuccess: (report) => set({ purchasingReport: null, sheetReport: report }),
  showSheet: (report) => set({ sheetReport: report }),
  showReveal: (report) => set({ revealReport: report }),
  setError: (message) => set({ purchasingReport: null, error: message }),
  dismissSheet: () => set({ sheetReport: null }),
  dismissReveal: () => set({ revealReport: null }),
  clearError: () => set({ error: null }),
}))
